import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, PointerEvent, WheelEvent } from "react";
import {
  pageWrapStyle,
  panelStyle,
  shellStyle,
  ActionButton,
  primaryButtonStyle,
  secondaryButtonStyle,
} from "../components/gangsheet/GangsheetUI";

type RGB = { r: number; g: number; b: number };
type PreviewMode = "original" | "artwork" | "mask";
type CanvasBg = "grid" | "white" | "black" | "gray";
type ActivePanel = "levels" | "edge" | "presets" | "export";
type ExportType = "transparent" | "mask" | "inverted-mask" | "black-preview" | "white-preview";
type SampleSize = 1 | 3 | 5 | 11;
type WorkflowKey = "upload" | "pick" | "tune" | "inspect" | "export";
type PresetKey = "auto-detect" | "remove-white" | "remove-black" | "keep-selected" | "hard" | "soft" | null;
type MaskArray = Uint8Array<ArrayBufferLike>;

type PixelInfo = {
  x: number;
  y: number;
  r: number;
  g: number;
  b: number;
  a: number;
} | null;

const DEFAULTS = {
  fuzziness: 23,
  threshold: 128,
  blackInput: 0,
  gamma: 100,
  whiteInput: 255,
  edgeFeather: 0,
  edgeExpand: 0,
  speckleCleanup: 0,
  edgeSmoothing: 0,
};

const MIN_ZOOM = 100;
const MAX_ZOOM = 51200;
const ZOOM_STEP = 100;

const WORKFLOW_HELP: Record<WorkflowKey, { label: string; help: string }> = {
  upload: {
    label: "Upload",
    help: "Start by choosing your artwork file. PNG is best because it can already contain transparency.",
  },
  pick: {
    label: "Pick",
    help: "Click Pick Color, then click the background color you want removed. Use 3px or 5px sample if the background is uneven.",
  },
  tune: {
    label: "Tune",
    help: "Adjust fuzziness, threshold, levels, and cleanup until the background disappears but artwork details stay clean.",
  },
  inspect: {
    label: "Inspect",
    help: "Zoom in to check edges and tiny pixels. Mouse wheel zooms. Hold right-click and drag to move around.",
  },
  export: {
    label: "Export",
    help: "Download the transparent PNG when it looks clean. Export uses the original image size, not the preview size.",
  },
};

export default function ColorKnockoutStudioPage() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewAreaRef = useRef<HTMLDivElement | null>(null);
  const sourceImageRef = useRef<HTMLImageElement | null>(null);
  const originalCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const uploadNonceRef = useRef(0);

  const panStateRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const [sourceId, setSourceId] = useState(0);
  const [renderVersion, setRenderVersion] = useState(0);
  const [previewAreaWidth, setPreviewAreaWidth] = useState(900);

  const [fileName, setFileName] = useState("");
  const [imageInfo, setImageInfo] = useState("");
  const [pickedColor, setPickedColor] = useState<RGB | null>(null);
  const [pixelInfo, setPixelInfo] = useState<PixelInfo>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const [sampleSize, setSampleSize] = useState<SampleSize>(1);
  const [fuzziness, setFuzziness] = useState(DEFAULTS.fuzziness);
  const [useThreshold, setUseThreshold] = useState(false);
  const [threshold, setThreshold] = useState(DEFAULTS.threshold);

  const [blackInput, setBlackInput] = useState(DEFAULTS.blackInput);
  const [gamma, setGamma] = useState(DEFAULTS.gamma);
  const [whiteInput, setWhiteInput] = useState(DEFAULTS.whiteInput);

  const [edgeFeather, setEdgeFeather] = useState(DEFAULTS.edgeFeather);
  const [edgeExpand, setEdgeExpand] = useState(DEFAULTS.edgeExpand);
  const [speckleCleanup, setSpeckleCleanup] = useState(DEFAULTS.speckleCleanup);
  const [edgeSmoothing, setEdgeSmoothing] = useState(DEFAULTS.edgeSmoothing);

  const [invertSelection, setInvertSelection] = useState(false);
  const [livePreview, setLivePreview] = useState(true);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("original");
  const [canvasBg, setCanvasBg] = useState<CanvasBg>("grid");
  const [activePanel, setActivePanel] = useState<ActivePanel>("levels");
  const [lastExportType, setLastExportType] = useState<ExportType | null>(null);
  const [activePreset, setActivePreset] = useState<PresetKey>(null);
  const [pickMode, setPickMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [activeHelp, setActiveHelp] = useState<WorkflowKey>("upload");
  const [status, setStatus] = useState("Upload artwork to begin.");

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  useEffect(() => {
    const area = previewAreaRef.current;
    if (!area) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setPreviewAreaWidth(width);
    });

    observer.observe(area);
    return () => observer.disconnect();
  }, []);

  const requestPreviewRender = useCallback(() => {
    setRenderVersion((value) => value + 1);
  }, []);

  const commitControlChange = useCallback(() => {
    setActivePreset(null);
    if (livePreview) requestPreviewRender();
  }, [livePreview, requestPreviewRender]);

  const resetProcessingControls = useCallback(() => {
    setPickedColor(null);
    setPixelInfo(null);
    setSampleSize(1);
    setFuzziness(DEFAULTS.fuzziness);
    setUseThreshold(false);
    setThreshold(DEFAULTS.threshold);
    setBlackInput(DEFAULTS.blackInput);
    setGamma(DEFAULTS.gamma);
    setWhiteInput(DEFAULTS.whiteInput);
    setEdgeFeather(DEFAULTS.edgeFeather);
    setEdgeExpand(DEFAULTS.edgeExpand);
    setSpeckleCleanup(DEFAULTS.speckleCleanup);
    setEdgeSmoothing(DEFAULTS.edgeSmoothing);
    setInvertSelection(false);
    setPreviewMode("original");
    setLastExportType(null);
    setActivePreset(null);
  }, []);

  const getColorDistance = (r: number, g: number, b: number, target: RGB) => {
    return Math.sqrt((r - target.r) ** 2 + (g - target.g) ** 2 + (b - target.b) ** 2);
  };

  const applyLevels = useCallback(
    (value: number) => {
      const safeWhite = Math.max(blackInput + 1, whiteInput);
      let normalized = (value - blackInput) / (safeWhite - blackInput);
      normalized = Math.max(0, Math.min(1, normalized));

      const gammaValue = Math.max(0.1, gamma / 100);
      normalized = Math.pow(normalized, 1 / gammaValue);

      return Math.round(normalized * 255);
    },
    [blackInput, gamma, whiteInput]
  );

  const prepareOriginalCanvas = useCallback((image: HTMLImageElement) => {
    const originalCanvas = document.createElement("canvas");
    originalCanvas.width = image.naturalWidth;
    originalCanvas.height = image.naturalHeight;

    const originalCtx = originalCanvas.getContext("2d", { willReadFrequently: true });
    if (!originalCtx) return false;

    originalCtx.imageSmoothingEnabled = false;
    originalCtx.clearRect(0, 0, originalCanvas.width, originalCanvas.height);
    originalCtx.drawImage(image, 0, 0);

    originalCanvasRef.current = originalCanvas;
    return true;
  }, []);

  const getSelectionStrength = useCallback(
    (r: number, g: number, b: number, target: RGB) => {
      const distance = getColorDistance(r, g, b, target);
      const softRange = Math.max(1, fuzziness);
      let strength = 1 - distance / softRange;
      strength = Math.max(0, Math.min(1, strength));

      if (useThreshold) {
        const gray = Math.round(strength * 255);
        strength = gray >= threshold ? 1 : 0;
      }

      if (invertSelection) strength = 1 - strength;

      return strength;
    },
    [fuzziness, useThreshold, threshold, invertSelection]
  );

  const growMask = (mask: MaskArray, width: number, height: number, amount: number): MaskArray => {
    let current = mask;

    for (let pass = 0; pass < amount; pass += 1) {
      const next: MaskArray = Uint8Array.from(current);

      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          const i = y * width + x;
          if (current[i]) continue;

          if (
            current[i - 1] ||
            current[i + 1] ||
            current[i - width] ||
            current[i + width] ||
            current[i - width - 1] ||
            current[i - width + 1] ||
            current[i + width - 1] ||
            current[i + width + 1]
          ) {
            next[i] = 1;
          }
        }
      }

      current = next;
    }

    return current;
  };

  const shrinkMask = (mask: MaskArray, width: number, height: number, amount: number): MaskArray => {
    let current = mask;

    for (let pass = 0; pass < amount; pass += 1) {
      const next: MaskArray = Uint8Array.from(current);

      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          const i = y * width + x;
          if (!current[i]) continue;

          if (
            !current[i - 1] ||
            !current[i + 1] ||
            !current[i - width] ||
            !current[i + width] ||
            !current[i - width - 1] ||
            !current[i - width + 1] ||
            !current[i + width - 1] ||
            !current[i + width + 1]
          ) {
            next[i] = 0;
          }
        }
      }

      current = next;
    }

    return current;
  };

  const cleanSpeckles = (mask: MaskArray, width: number, height: number, amount: number): MaskArray => {
    if (amount <= 0) return mask;

    const minNeighbors = amount === 1 ? 1 : amount === 2 ? 2 : 3;
    const next: MaskArray = Uint8Array.from(mask);

    for (let y = 1; y < height - 1; y += 1) {
      for (let x = 1; x < width - 1; x += 1) {
        const i = y * width + x;
        if (!mask[i]) continue;

        let neighbors = 0;
        neighbors += mask[i - 1];
        neighbors += mask[i + 1];
        neighbors += mask[i - width];
        neighbors += mask[i + width];
        neighbors += mask[i - width - 1];
        neighbors += mask[i - width + 1];
        neighbors += mask[i + width - 1];
        neighbors += mask[i + width + 1];

        if (neighbors <= minNeighbors) next[i] = 0;
      }
    }

    return next;
  };

  const smoothMask = (mask: MaskArray, width: number, height: number, amount: number): MaskArray => {
    let current = mask;

    for (let pass = 0; pass < amount; pass += 1) {
      const next: MaskArray = Uint8Array.from(current);

      for (let y = 1; y < height - 1; y += 1) {
        for (let x = 1; x < width - 1; x += 1) {
          const i = y * width + x;

          let neighbors = 0;
          neighbors += current[i - 1];
          neighbors += current[i + 1];
          neighbors += current[i - width];
          neighbors += current[i + width];
          neighbors += current[i - width - 1];
          neighbors += current[i - width + 1];
          neighbors += current[i + width - 1];
          neighbors += current[i + width + 1];

          if (current[i] && neighbors <= 2) next[i] = 0;
          if (!current[i] && neighbors >= 6) next[i] = 1;
        }
      }

      current = next;
    }

    return current;
  };

  const buildFinalMask = useCallback(
    (sourceData: Uint8ClampedArray, width: number, height: number, target: RGB) => {
      let mask: MaskArray = new Uint8Array(width * height);

      for (let i = 0; i < sourceData.length; i += 4) {
        const pixelIndex = i / 4;
        const alpha = sourceData[i + 3];

        if (alpha === 0) {
          mask[pixelIndex] = 0;
          continue;
        }

        mask[pixelIndex] =
          getSelectionStrength(sourceData[i], sourceData[i + 1], sourceData[i + 2], target) > 0.5
            ? 1
            : 0;
      }

      if (speckleCleanup > 0) mask = cleanSpeckles(mask, width, height, speckleCleanup);
      if (edgeSmoothing > 0) mask = smoothMask(mask, width, height, edgeSmoothing);
      if (edgeExpand > 0) mask = growMask(mask, width, height, edgeExpand);
      if (edgeExpand < 0) mask = shrinkMask(mask, width, height, Math.abs(edgeExpand));

      return mask;
    },
    [getSelectionStrength, speckleCleanup, edgeSmoothing, edgeExpand]
  );

  const getFeatheredMaskValue = useCallback(
    (mask: MaskArray, width: number, height: number, x: number, y: number) => {
      const i = y * width + x;

      if (edgeFeather <= 0) return mask[i] ? 255 : 0;

      let selectedCount = 0;
      let total = 0;
      const radius = edgeFeather;

      for (let yy = Math.max(0, y - radius); yy <= Math.min(height - 1, y + radius); yy += 1) {
        for (let xx = Math.max(0, x - radius); xx <= Math.min(width - 1, x + radius); xx += 1) {
          const dx = xx - x;
          const dy = yy - y;

          if (dx * dx + dy * dy <= radius * radius) {
            selectedCount += mask[yy * width + xx];
            total += 1;
          }
        }
      }

      return Math.round((selectedCount / Math.max(1, total)) * 255);
    },
    [edgeFeather]
  );

  const renderToCanvas = useCallback(
    (targetCanvas: HTMLCanvasElement, mode: PreviewMode, background?: "black" | "white") => {
      const image = sourceImageRef.current;
      const originalCanvas = originalCanvasRef.current;

      if (!image || !originalCanvas) return;

      const ctx = targetCanvas.getContext("2d", { willReadFrequently: true });
      const originalCtx = originalCanvas.getContext("2d", { willReadFrequently: true });
      if (!ctx || !originalCtx) return;

      const width = image.naturalWidth;
      const height = image.naturalHeight;

      targetCanvas.width = width;
      targetCanvas.height = height;

      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, width, height);

      if (background === "black") {
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, width, height);
      }

      if (background === "white") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
      }

      ctx.drawImage(originalCanvas, 0, 0);

      if (mode === "original" || !pickedColor) return;

      const imageData = ctx.getImageData(0, 0, width, height);
      const sourceData = originalCtx.getImageData(0, 0, width, height).data;
      const data = imageData.data;
      const finalMask = buildFinalMask(sourceData, width, height, pickedColor);

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const pixelIndex = y * width + x;
          const i = pixelIndex * 4;
          const originalAlpha = sourceData[i + 3];

          if (originalAlpha === 0 && !background) continue;

          const maskValue = getFeatheredMaskValue(finalMask, width, height, x, y);
          const removeStrength = maskValue / 255;

          if (mode === "mask") {
            data[i] = maskValue;
            data[i + 1] = maskValue;
            data[i + 2] = maskValue;
            data[i + 3] = 255;
          } else {
            data[i] = applyLevels(sourceData[i]);
            data[i + 1] = applyLevels(sourceData[i + 1]);
            data[i + 2] = applyLevels(sourceData[i + 2]);

            if (background) {
              const bgValue = background === "black" ? 0 : 255;
              data[i] = Math.round(data[i] * (1 - removeStrength) + bgValue * removeStrength);
              data[i + 1] = Math.round(data[i + 1] * (1 - removeStrength) + bgValue * removeStrength);
              data[i + 2] = Math.round(data[i + 2] * (1 - removeStrength) + bgValue * removeStrength);
              data[i + 3] = 255;
            } else {
              data[i + 3] = Math.round(originalAlpha * (1 - removeStrength));
            }
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
    },
    [pickedColor, buildFinalMask, getFeatheredMaskValue, applyLevels]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded) return;

    window.requestAnimationFrame(() => {
      renderToCanvas(canvas, previewMode);
    });
  }, [sourceId, renderVersion, imageLoaded, previewMode, renderToCanvas]);

  const getPreviewDisplaySize = () => {
    const image = sourceImageRef.current;
    if (!image) return { width: 0, height: 0 };

    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;

    const availableWidth = Math.max(240, previewAreaWidth - 24);
    const availableHeight = Math.max(220, window.innerHeight - 320);

    const fitScale = Math.min(availableWidth / naturalWidth, availableHeight / naturalHeight, 1);
    const zoomScale = zoom / 100;
    const finalScale = fitScale * zoomScale;

    return {
      width: Math.max(1, Math.round(naturalWidth * finalScale)),
      height: Math.max(1, Math.round(naturalHeight * finalScale)),
    };
  };

  const loadImage = (file: File) => {
    uploadNonceRef.current += 1;
    const currentNonce = uploadNonceRef.current;

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 1;
      canvas.height = 1;
    }

    sourceImageRef.current = null;
    originalCanvasRef.current = null;
    panStateRef.current.active = false;

    setImageLoaded(false);
    setFileName("");
    setImageInfo("");
    setPickedColor(null);
    setPixelInfo(null);
    setPickMode(false);
    setZoom(100);
    resetProcessingControls();
    setActiveHelp("upload");
    setStatus("Loading fresh artwork...");

    const imageUrl = URL.createObjectURL(file);
    objectUrlRef.current = imageUrl;

    const img = new Image();

    img.onload = () => {
      if (currentNonce !== uploadNonceRef.current) {
        URL.revokeObjectURL(imageUrl);
        return;
      }

      sourceImageRef.current = img;

      const prepared = prepareOriginalCanvas(img);

      if (!prepared) {
        setStatus("Could not prepare image canvas.");
        return;
      }

      setFileName(file.name);
      setImageInfo(`${img.naturalWidth} × ${img.naturalHeight}px • export unchanged`);
      setPickedColor(null);
      setPixelInfo(null);
      setImageLoaded(true);
      setPreviewMode("original");
      setPickMode(false);
      setZoom(100);
      setSourceId((value) => value + 1);
      setActiveHelp("pick");
      setStatus("Fresh artwork loaded. Next: click Pick Color, then click the background you want removed.");
    };

    img.onerror = () => {
      if (currentNonce !== uploadNonceRef.current) return;
      setStatus("Image failed to load. Try PNG, JPG, or WebP.");
    };

    img.src = imageUrl;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    loadImage(file);
    event.target.value = "";
  };

  const getCanvasPixelPosition = (event: PointerEvent<HTMLCanvasElement>) => {
    const previewCanvas = canvasRef.current;
    if (!previewCanvas) return null;

    const rect = previewCanvas.getBoundingClientRect();
    const x = Math.floor((event.clientX - rect.left) * (previewCanvas.width / rect.width));
    const y = Math.floor((event.clientY - rect.top) * (previewCanvas.height / rect.height));

    if (x < 0 || y < 0 || x >= previewCanvas.width || y >= previewCanvas.height) return null;

    return { x, y };
  };

  const sampleColorFromOriginal = useCallback(
    (x: number, y: number) => {
      const originalCanvas = originalCanvasRef.current;
      if (!originalCanvas) return null;

      const originalCtx = originalCanvas.getContext("2d", { willReadFrequently: true });
      if (!originalCtx) return null;

      const radius = Math.floor(sampleSize / 2);
      let totalR = 0;
      let totalG = 0;
      let totalB = 0;
      let totalA = 0;
      let count = 0;

      for (let yy = Math.max(0, y - radius); yy <= Math.min(originalCanvas.height - 1, y + radius); yy += 1) {
        for (let xx = Math.max(0, x - radius); xx <= Math.min(originalCanvas.width - 1, x + radius); xx += 1) {
          const pixel = originalCtx.getImageData(xx, yy, 1, 1).data;
          totalR += pixel[0];
          totalG += pixel[1];
          totalB += pixel[2];
          totalA += pixel[3];
          count += 1;
        }
      }

      if (!count) return null;

      return {
        r: Math.round(totalR / count),
        g: Math.round(totalG / count),
        b: Math.round(totalB / count),
        a: Math.round(totalA / count),
      };
    },
    [sampleSize]
  );

  const updatePixelInspector = (event: PointerEvent<HTMLCanvasElement>) => {
    const pos = getCanvasPixelPosition(event);
    if (!pos) return;

    const color = sampleColorFromOriginal(pos.x, pos.y);
    if (!color) return;

    setPixelInfo({
      x: pos.x,
      y: pos.y,
      r: color.r,
      g: color.g,
      b: color.b,
      a: color.a,
    });
  };

  const pickColorFromPointer = (event: PointerEvent<HTMLCanvasElement>) => {
    const pos = getCanvasPixelPosition(event);

    if (!pos) {
      setStatus("Could not read that pixel.");
      return;
    }

    const color = sampleColorFromOriginal(pos.x, pos.y);

    if (!color) {
      setStatus("Upload artwork first.");
      return;
    }

    const selected = { r: color.r, g: color.g, b: color.b };

    setPickedColor(selected);
    setPixelInfo({
      x: pos.x,
      y: pos.y,
      r: color.r,
      g: color.g,
      b: color.b,
      a: color.a,
    });
    setPreviewMode("artwork");
    setPickMode(false);
    setActivePreset(null);
    setActiveHelp("tune");
    setStatus(`Picked RGB(${selected.r}, ${selected.g}, ${selected.b}). Now adjust fuzziness and cleanup until it looks right.`);
    requestPreviewRender();
  };

  const handleCanvasPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    updatePixelInspector(event);

    if (pickMode && event.button === 0) {
      event.preventDefault();
      pickColorFromPointer(event);
    }
  };

  const handleCanvasPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!imageLoaded) return;
    updatePixelInspector(event);
  };

  const handlePreviewPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!imageLoaded || event.button !== 2) return;

    const area = previewAreaRef.current;
    if (!area) return;

    panStateRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: area.scrollLeft,
      scrollTop: area.scrollTop,
    };

    area.setPointerCapture(event.pointerId);
    setIsPanning(true);
    event.preventDefault();
  };

  const handlePreviewPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const area = previewAreaRef.current;
    const pan = panStateRef.current;

    if (!area || !pan.active || pan.pointerId !== event.pointerId) return;

    const dx = event.clientX - pan.startX;
    const dy = event.clientY - pan.startY;

    area.scrollLeft = pan.scrollLeft - dx;
    area.scrollTop = pan.scrollTop - dy;

    event.preventDefault();
  };

  const finishPanning = (event: PointerEvent<HTMLDivElement>) => {
    const area = previewAreaRef.current;
    const pan = panStateRef.current;

    if (!area || !pan.active || pan.pointerId !== event.pointerId) return;

    pan.active = false;
    setIsPanning(false);

    if (area.hasPointerCapture(event.pointerId)) {
      area.releasePointerCapture(event.pointerId);
    }

    event.preventDefault();
  };

  const handlePreviewWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!imageLoaded) return;

    event.preventDefault();

    const direction = event.deltaY > 0 ? -1 : 1;
    const step = event.ctrlKey ? 1000 : event.shiftKey ? 500 : 100;

    setZoom((currentZoom) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, currentZoom + direction * step)));
  };

  const applyPreset = (preset: Exclude<PresetKey, "auto-detect" | null>) => {
    setActivePreset(preset);

    if (preset === "remove-white") {
      setPickedColor({ r: 255, g: 255, b: 255 });
      setFuzziness(80);
      setUseThreshold(false);
      setThreshold(128);
      setBlackInput(0);
      setGamma(100);
      setWhiteInput(255);
      setInvertSelection(false);
      setEdgeFeather(0);
      setEdgeExpand(0);
      setSpeckleCleanup(2);
      setEdgeSmoothing(1);
      setPreviewMode("artwork");
      setStatus("Preset applied: white background removal. Inspect edges before export.");
    }

    if (preset === "remove-black") {
      setPickedColor({ r: 0, g: 0, b: 0 });
      setFuzziness(80);
      setUseThreshold(false);
      setThreshold(128);
      setBlackInput(0);
      setGamma(100);
      setWhiteInput(255);
      setInvertSelection(false);
      setEdgeFeather(0);
      setEdgeExpand(0);
      setSpeckleCleanup(2);
      setEdgeSmoothing(1);
      setPreviewMode("artwork");
      setStatus("Preset applied: black background removal. Inspect edges before export.");
    }

    if (preset === "keep-selected") {
      setInvertSelection(true);
      setFuzziness(60);
      setUseThreshold(false);
      setThreshold(128);
      setEdgeFeather(0);
      setEdgeExpand(0);
      setSpeckleCleanup(1);
      setEdgeSmoothing(1);
      setPreviewMode("artwork");
      setStatus("Preset applied: keeping selected color range only.");
    }

    if (preset === "hard") {
      setFuzziness(90);
      setUseThreshold(true);
      setThreshold(128);
      setBlackInput(0);
      setGamma(100);
      setWhiteInput(255);
      setEdgeFeather(0);
      setEdgeExpand(0);
      setSpeckleCleanup(0);
      setEdgeSmoothing(0);
      setPreviewMode("artwork");
      setStatus("Preset applied: hard 128 threshold mask.");
    }

    if (preset === "soft") {
      setFuzziness(110);
      setUseThreshold(false);
      setThreshold(128);
      setBlackInput(0);
      setGamma(100);
      setWhiteInput(255);
      setEdgeFeather(1);
      setEdgeExpand(0);
      setSpeckleCleanup(2);
      setEdgeSmoothing(2);
      setPreviewMode("artwork");
      setStatus("Preset applied: soft cleanup. Watch for softened details.");
    }

    setActiveHelp("inspect");
    requestPreviewRender();
  };

  const autoDetectBackground = () => {
    const originalCanvas = originalCanvasRef.current;
    if (!originalCanvas) {
      setStatus("Upload artwork first.");
      return;
    }

    const ctx = originalCanvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const points = [
      [0, 0],
      [originalCanvas.width - 1, 0],
      [0, originalCanvas.height - 1],
      [originalCanvas.width - 1, originalCanvas.height - 1],
    ];

    let r = 0;
    let g = 0;
    let b = 0;

    points.forEach(([x, y]) => {
      const pixel = ctx.getImageData(x, y, 1, 1).data;
      r += pixel[0];
      g += pixel[1];
      b += pixel[2];
    });

    const picked = {
      r: Math.round(r / points.length),
      g: Math.round(g / points.length),
      b: Math.round(b / points.length),
    };

    setPickedColor(picked);
    setFuzziness(75);
    setUseThreshold(false);
    setInvertSelection(false);
    setEdgeFeather(0);
    setEdgeExpand(0);
    setSpeckleCleanup(2);
    setEdgeSmoothing(1);
    setPreviewMode("artwork");
    setActivePreset("auto-detect");
    setActiveHelp("tune");
    setStatus(`Auto detected corner background RGB(${picked.r}, ${picked.g}, ${picked.b}). Adjust fuzziness if needed.`);
    requestPreviewRender();
  };

  const resetArtwork = () => {
    resetProcessingControls();
    setPickMode(false);
    setZoom(100);
    setActiveHelp("upload");
    setStatus("Artwork reset. Upload or pick a color to start again.");
    requestPreviewRender();
  };

  const exportFile = (type: ExportType) => {
    const image = sourceImageRef.current;

    if (!image) {
      setStatus("Nothing to export yet.");
      return;
    }

    if (type !== "black-preview" && type !== "white-preview" && type !== "mask" && !pickedColor) {
      setStatus("Pick a color or use an auto preset before exporting transparent artwork.");
      return;
    }

    const exportCanvas = document.createElement("canvas");

    if (type === "transparent") renderToCanvas(exportCanvas, "artwork");
    if (type === "mask") renderToCanvas(exportCanvas, "mask");
    if (type === "black-preview") renderToCanvas(exportCanvas, "artwork", "black");
    if (type === "white-preview") renderToCanvas(exportCanvas, "artwork", "white");

    if (type === "inverted-mask") {
      if (!pickedColor) {
        setStatus("Pick a color before exporting an inverted mask.");
        return;
      }

      renderToCanvas(exportCanvas, "mask");

      const ctx = exportCanvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      const imageData = ctx.getImageData(0, 0, exportCanvas.width, exportCanvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const inverted = 255 - data[i];
        data[i] = inverted;
        data[i + 1] = inverted;
        data[i + 2] = inverted;
        data[i + 3] = 255;
      }

      ctx.putImageData(imageData, 0, 0);
    }

    const safeBaseName = fileName
      ? fileName.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase()
      : "color-knockout";

    const names: Record<ExportType, string> = {
      transparent: `${safeBaseName}-transparent-original-size.png`,
      mask: `${safeBaseName}-mask-original-size.png`,
      "inverted-mask": `${safeBaseName}-inverted-mask-original-size.png`,
      "black-preview": `${safeBaseName}-black-preview-original-size.png`,
      "white-preview": `${safeBaseName}-white-preview-original-size.png`,
    };

    setLastExportType(type);
    setActiveHelp("export");

    const link = document.createElement("a");
    link.download = names[type];
    link.href = exportCanvas.toDataURL("image/png");
    link.click();

    setStatus(`Exported ${names[type]} at original ${image.naturalWidth} × ${image.naturalHeight}px.`);
  };

  const getImageBackgroundStyle = (): CSSProperties => {
    if (canvasBg === "white") return { background: "#ffffff" };
    if (canvasBg === "black") return { background: "#020617" };
    if (canvasBg === "gray") return { background: "#94a3b8" };

    return {
      backgroundColor: "#f8fafc",
      backgroundImage: `
        linear-gradient(45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(-45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #d1d5db 75%),
        linear-gradient(-45deg, transparent 75%, #d1d5db 75%)
      `,
      backgroundSize: "22px 22px",
      backgroundPosition: "0 0, 0 11px, 11px -11px, -11px 0px",
    };
  };

  const getCanvasCursor = () => {
    if (!imageLoaded) return "default";
    if (pickMode) return "crosshair";
    if (isPanning) return "grabbing";
    return "default";
  };

  const previewDisplaySize = getPreviewDisplaySize();

  return (
    <div style={pageWrapStyle}>
      <div style={{ ...shellStyle, maxWidth: 1760 }}>
        <div style={{ ...panelStyle, padding: 8 }}>
          <div style={workflowBarStyle}>
            {(Object.keys(WORKFLOW_HELP) as WorkflowKey[]).map((key) => (
              <WorkflowStep
                key={key}
                active={activeHelp === key}
                label={WORKFLOW_HELP[key].label}
                help={WORKFLOW_HELP[key].help}
                onClick={() => {
                  setActiveHelp(key);
                  setStatus(WORKFLOW_HELP[key].help);
                }}
              />
            ))}
          </div>

          <div style={helpBarStyle}>
            <span style={{ color: "#e0f2fe", fontWeight: 900 }}>{WORKFLOW_HELP[activeHelp].label}:</span>
            <span>{WORKFLOW_HELP[activeHelp].help}</span>
          </div>
        </div>

        <div style={studioGridStyle}>
          <div style={{ ...panelStyle, padding: 12, display: "grid", gap: 9, alignContent: "start" }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#eff6ff" }}>Main Controls</div>

            <label style={{ display: "grid", gap: 7 }}>
              <span style={labelStyle}>Upload Artwork</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileChange}
                style={fileInputStyle}
              />
            </label>

            <ActionButton style={secondaryButtonStyle} onClick={() => fileInputRef.current?.click()}>
              Reload Image
            </ActionButton>

            <ActionButton
              style={pickMode ? primaryButtonStyle : secondaryButtonStyle}
              onClick={() => {
                setPickMode((value) => !value);
                setActiveHelp("pick");
                setStatus(!pickMode ? "Pick Color enabled. Left click the background color you want removed." : "Pick Color disabled.");
              }}
            >
              {pickMode ? "Pick Color On" : "Pick Color"}
            </ActionButton>

            <div style={pickedColorCardStyle}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 13,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: pickedColor
                    ? `rgb(${pickedColor.r}, ${pickedColor.g}, ${pickedColor.b})`
                    : "rgba(255,255,255,0.08)",
                }}
              />
              <div>
                <div style={{ color: "#eff6ff", fontWeight: 900, fontSize: 13 }}>Picked Color</div>
                <div style={{ color: "#93c5fd", fontSize: 12, marginTop: 2 }}>
                  {pickedColor ? `RGB(${pickedColor.r}, ${pickedColor.g}, ${pickedColor.b})` : "Click Pick Color first"}
                </div>
              </div>
            </div>

            <div style={sampleGridStyle}>
              {([1, 3, 5, 11] as SampleSize[]).map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => {
                    setSampleSize(size);
                    setActivePreset(null);
                    setStatus(`${size}px sample selected. Larger samples help with uneven backgrounds.`);
                  }}
                  style={sampleSize === size ? smallActiveButtonStyle : smallButtonStyle}
                  title={`${size}px sample size`}
                >
                  {size}px
                </button>
              ))}
            </div>

            <div style={compactStackStyle}>
              <PrecisionSlider label="Fuzziness" value={fuzziness} min={0} max={255} step={1} onChange={setFuzziness} onCommit={commitControlChange} />
              <PrecisionSlider label="Threshold" value={threshold} min={0} max={255} step={1} onChange={setThreshold} onCommit={commitControlChange} />
            </div>

            <div style={toggleGridStyle}>
              <label style={toggleCardStyle}>
                <input
                  type="checkbox"
                  checked={useThreshold}
                  onChange={(event) => {
                    setUseThreshold(event.target.checked);
                    commitControlChange();
                  }}
                />
                Hard Threshold
              </label>

              <label style={toggleCardStyle}>
                <input
                  type="checkbox"
                  checked={invertSelection}
                  onChange={(event) => {
                    setInvertSelection(event.target.checked);
                    commitControlChange();
                  }}
                />
                Invert
              </label>
            </div>

            <div style={previewButtonGridStyle}>
              <ActionButton style={previewMode === "original" ? primaryButtonStyle : secondaryButtonStyle} onClick={() => setPreviewMode("original")}>
                Original
              </ActionButton>
              <ActionButton style={previewMode === "artwork" ? primaryButtonStyle : secondaryButtonStyle} onClick={() => setPreviewMode("artwork")}>
                Preview
              </ActionButton>
              <ActionButton style={previewMode === "mask" ? primaryButtonStyle : secondaryButtonStyle} onClick={() => setPreviewMode("mask")}>
                Mask
              </ActionButton>
            </div>

            <div style={pixelCardStyle}>
              <div style={{ color: "#eff6ff", fontWeight: 900, fontSize: 12 }}>Pixel Inspector</div>
              <div style={{ color: "#93c5fd", fontSize: 11, lineHeight: 1.45 }}>
                {pixelInfo
                  ? `X:${pixelInfo.x} Y:${pixelInfo.y} • RGB(${pixelInfo.r}, ${pixelInfo.g}, ${pixelInfo.b}) • A:${pixelInfo.a}`
                  : "Move over artwork to inspect pixels."}
              </div>
            </div>

            <ActionButton style={secondaryButtonStyle} onClick={resetArtwork}>
              Reset Controls
            </ActionButton>
          </div>

          <div style={{ ...panelStyle, padding: 12, display: "grid", gap: 9, minWidth: 0, alignContent: "start" }}>
            <div style={previewHeaderStyle}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#eff6ff" }}>Preview Canvas</div>
                <div style={{ color: "#93c5fd", marginTop: 3, fontSize: 12 }}>
                  {fileName || "No file loaded"} {imageInfo ? `• ${imageInfo}` : ""}
                </div>
              </div>

              {imageLoaded && (
                <div style={previewToolsStyle}>
                  <div style={backgroundButtonRowStyle}>
                    {(["grid", "white", "black", "gray"] as CanvasBg[]).map((bg) => (
                      <ActionButton key={bg} style={canvasBg === bg ? primaryButtonStyle : secondaryButtonStyle} onClick={() => setCanvasBg(bg)}>
                        {bg}
                      </ActionButton>
                    ))}
                  </div>

                  <div style={zoomControlStyle}>
                    <span style={zoomLabelStyle}>Zoom</span>

                    <ActionButton style={secondaryButtonStyle} onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP))}>
                      -
                    </ActionButton>

                    <input
                      type="range"
                      min={MIN_ZOOM}
                      max={MAX_ZOOM}
                      step={ZOOM_STEP}
                      value={zoom}
                      onChange={(event) => setZoom(Number(event.target.value))}
                      style={{ width: 190, accentColor: "#22d3ee" }}
                    />

                    <ActionButton style={secondaryButtonStyle} onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP))}>
                      +
                    </ActionButton>

                    <input
                      type="number"
                      min={MIN_ZOOM}
                      max={MAX_ZOOM}
                      step={100}
                      value={zoom}
                      onChange={(event) => {
                        const raw = Number(event.target.value);
                        if (!Number.isFinite(raw)) return;
                        setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, raw)));
                      }}
                      onBlur={() => setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(z / 100) * 100)))}
                      style={zoomInputStyle}
                    />

                    <span style={zoomPercentStyle}>%</span>
                  </div>
                </div>
              )}
            </div>

            <div
              ref={previewAreaRef}
              onWheel={handlePreviewWheel}
              onContextMenu={(event) => event.preventDefault()}
              onPointerDown={handlePreviewPointerDown}
              onPointerMove={handlePreviewPointerMove}
              onPointerUp={finishPanning}
              onPointerCancel={finishPanning}
              onPointerLeave={(event) => {
                if (panStateRef.current.active) finishPanning(event);
              }}
              style={{
                width: "100%",
                height: imageLoaded ? "calc(100vh - 335px)" : 300,
                minHeight: imageLoaded ? 310 : 260,
                maxHeight: imageLoaded ? "calc(100vh - 335px)" : 300,
                overflow: "auto",
                borderRadius: 18,
                border: "1px solid rgba(96,165,250,0.15)",
                padding: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(2,6,23,0.32)",
                cursor: getCanvasCursor(),
                touchAction: "none",
                userSelect: "none",
              }}
            >
              {!imageLoaded && <div style={emptyCanvasStyle}>Upload artwork to preview</div>}

              {imageLoaded && (
                <div
                  style={{
                    width: previewDisplaySize.width,
                    height: previewDisplaySize.height,
                    flex: "0 0 auto",
                    borderRadius: 14,
                    overflow: "hidden",
                    boxShadow: "0 20px 60px rgba(2,6,23,0.35)",
                    imageRendering: "pixelated",
                    ...getImageBackgroundStyle(),
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    onPointerDown={handleCanvasPointerDown}
                    onPointerMove={handleCanvasPointerMove}
                    style={{
                      display: "block",
                      width: previewDisplaySize.width,
                      height: previewDisplaySize.height,
                      cursor: getCanvasCursor(),
                      imageRendering: "pixelated",
                      touchAction: "none",
                    }}
                  />
                </div>
              )}
            </div>

            <div style={statusCardStyle}>
              <div style={statusTitleRowStyle}>
                <span style={statusDotStyle} />
                <span style={statusTitleStyle}>Current Step</span>
              </div>
              <div style={statusTextStyle}>{status}</div>
            </div>
          </div>

          <div style={{ ...panelStyle, padding: 12, display: "grid", gap: 9, alignContent: "start" }}>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#eff6ff" }}>Tool Controls</div>

            <div style={tabBarStyle}>
              <ToolTab active={activePanel === "levels"} label="Levels" onClick={() => setActivePanel("levels")} />
              <ToolTab active={activePanel === "edge"} label="Edge" onClick={() => setActivePanel("edge")} />
              <ToolTab active={activePanel === "presets"} label="Presets" onClick={() => setActivePanel("presets")} />
              <ToolTab active={activePanel === "export"} label="Export" onClick={() => setActivePanel("export")} />
            </div>

            <div style={toggleGridStyle}>
              <label style={toggleCardStyle}>
                <input type="checkbox" checked={livePreview} onChange={(event) => setLivePreview(event.target.checked)} />
                Live Preview
              </label>

              <ActionButton style={primaryButtonStyle} onClick={requestPreviewRender}>
                Apply Preview
              </ActionButton>
            </div>

            {activePanel === "levels" && (
              <div style={compactPanelBodyStyle}>
                <PanelHeader title="Levels" text="Type exact values or drag sliders." />
                <div style={sliderGridStyle}>
                  <PrecisionSlider label="Black Input" value={blackInput} min={0} max={254} step={1} onChange={setBlackInput} onCommit={commitControlChange} />
                  <PrecisionSlider label="Gamma" value={gamma} min={10} max={300} step={1} onChange={setGamma} onCommit={commitControlChange} display={(gamma / 100).toFixed(2)} />
                  <PrecisionSlider label="White Input" value={whiteInput} min={1} max={255} step={1} onChange={setWhiteInput} onCommit={commitControlChange} />
                </div>
              </div>
            )}

            {activePanel === "edge" && (
              <div style={compactPanelBodyStyle}>
                <PanelHeader title="Edge Cleanup" text="Small values first. Heavy cleanup can eat detail." />
                <div style={sliderGridStyle}>
                  <PrecisionSlider label="Feather Edge" value={edgeFeather} min={0} max={6} step={1} onChange={setEdgeFeather} onCommit={commitControlChange} />
                  <PrecisionSlider label="Choke / Expand" value={edgeExpand} min={-6} max={6} step={1} onChange={setEdgeExpand} onCommit={commitControlChange} />
                  <PrecisionSlider label="Remove Speckles" value={speckleCleanup} min={0} max={3} step={1} onChange={setSpeckleCleanup} onCommit={commitControlChange} />
                  <PrecisionSlider label="Smooth Edge" value={edgeSmoothing} min={0} max={3} step={1} onChange={setEdgeSmoothing} onCommit={commitControlChange} />
                </div>
              </div>
            )}

            {activePanel === "presets" && (
              <div style={compactPanelBodyStyle}>
                <PanelHeader title="Presets" text="Fast starting points for common artwork." />
                <div style={cardButtonGridStyle}>
                  <PremiumOptionButton title="Auto Detect" text="Samples image corners." active={activePreset === "auto-detect"} onClick={autoDetectBackground} />
                  <PremiumOptionButton title="Remove White" text="Fuzz 80 • cleanup 2." active={activePreset === "remove-white"} onClick={() => applyPreset("remove-white")} />
                  <PremiumOptionButton title="Remove Black" text="Fuzz 80 • cleanup 2." active={activePreset === "remove-black"} onClick={() => applyPreset("remove-black")} />
                  <PremiumOptionButton title="Keep Selected" text="Invert selected range." active={activePreset === "keep-selected"} onClick={() => applyPreset("keep-selected")} />
                  <PremiumOptionButton title="Hard 128" text="Hard threshold cutoff." active={activePreset === "hard"} onClick={() => applyPreset("hard")} />
                  <PremiumOptionButton title="Soft Clean" text="Fuzz 110 • soft edge." active={activePreset === "soft"} onClick={() => applyPreset("soft")} />
                </div>
              </div>
            )}

            {activePanel === "export" && (
              <div style={compactPanelBodyStyle}>
                <PanelHeader title="Export Options" text="Exports use original image pixels, not preview size." />
                <div style={cardButtonGridStyle}>
                  <PremiumOptionButton title="Transparent PNG" text="Final clean artwork." primary active={lastExportType === "transparent"} onClick={() => exportFile("transparent")} />
                  <PremiumOptionButton title="Mask PNG" text="Black/white mask." active={lastExportType === "mask"} onClick={() => exportFile("mask")} />
                  <PremiumOptionButton title="Inverted Mask" text="Opposite mask." active={lastExportType === "inverted-mask"} onClick={() => exportFile("inverted-mask")} />
                  <PremiumOptionButton title="Black Preview" text="Proof on black." active={lastExportType === "black-preview"} onClick={() => exportFile("black-preview")} />
                  <PremiumOptionButton title="White Preview" text="Proof on white." active={lastExportType === "white-preview"} onClick={() => exportFile("white-preview")} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const workflowBarStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 8,
};

const helpBarStyle: CSSProperties = {
  marginTop: 8,
  padding: "8px 10px",
  borderRadius: 13,
  border: "1px solid rgba(96,165,250,0.16)",
  background: "rgba(15,23,42,0.62)",
  color: "#93c5fd",
  fontSize: 12,
  fontWeight: 800,
  display: "flex",
  gap: 7,
  alignItems: "center",
  lineHeight: 1.35,
};

const studioGridStyle: CSSProperties = {
  display: "grid",
  gap: 12,
  gridTemplateColumns: "286px minmax(0, 1fr) 316px",
  alignItems: "start",
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 900,
  color: "#dbeafe",
};

const fileInputStyle: CSSProperties = {
  width: "100%",
  color: "#dbeafe",
  background: "rgba(15,23,42,0.75)",
  border: "1px solid rgba(96,165,250,0.18)",
  borderRadius: 14,
  padding: 9,
};

const pickedColorCardStyle: CSSProperties = {
  padding: 9,
  borderRadius: 16,
  border: "1px solid rgba(96,165,250,0.18)",
  background: "rgba(15,23,42,0.7)",
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const pixelCardStyle: CSSProperties = {
  padding: 9,
  borderRadius: 15,
  border: "1px solid rgba(96,165,250,0.18)",
  background: "rgba(15,23,42,0.7)",
  display: "grid",
  gap: 4,
};

const compactStackStyle: CSSProperties = {
  display: "grid",
  gap: 8,
};

const sampleGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 7,
};

const toggleGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
};

const toggleCardStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 9px",
  borderRadius: 14,
  border: "1px solid rgba(96,165,250,0.18)",
  background: "rgba(15,23,42,0.7)",
  color: "#dbeafe",
  fontSize: 12,
  fontWeight: 900,
};

const previewButtonGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 7,
};

const previewHeaderStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const previewToolsStyle: CSSProperties = {
  display: "grid",
  gap: 7,
  justifyItems: "end",
};

const backgroundButtonRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  gap: 7,
};

const zoomControlStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 7,
  padding: 7,
  borderRadius: 16,
  border: "1px solid rgba(96,165,250,0.16)",
  background: "rgba(15,23,42,0.68)",
};

const zoomLabelStyle: CSSProperties = {
  color: "#bfdbfe",
  fontSize: 12,
  fontWeight: 900,
};

const zoomInputStyle: CSSProperties = {
  width: 82,
  padding: "8px 7px",
  borderRadius: 13,
  border: "1px solid rgba(96,165,250,0.28)",
  background: "rgba(15,23,42,0.86)",
  color: "#e0f2fe",
  fontSize: 13,
  fontWeight: 900,
  textAlign: "center",
};

const zoomPercentStyle: CSSProperties = {
  color: "#93c5fd",
  fontSize: 12,
  fontWeight: 900,
};

const smallButtonStyle: CSSProperties = {
  border: "1px solid rgba(96,165,250,0.18)",
  background: "rgba(15,23,42,0.72)",
  color: "#dbeafe",
  borderRadius: 12,
  padding: "7px 8px",
  fontWeight: 900,
  fontSize: 11,
  cursor: "pointer",
};

const smallActiveButtonStyle: CSSProperties = {
  ...smallButtonStyle,
  border: "1px solid rgba(34,211,238,0.55)",
  background: "linear-gradient(180deg, rgba(37,99,235,0.98), rgba(29,78,216,0.86))",
  color: "#ffffff",
};

const emptyCanvasStyle: CSSProperties = {
  color: "#64748b",
  fontWeight: 900,
  fontSize: 14,
  padding: "12px 16px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.88)",
  border: "1px solid rgba(148,163,184,0.45)",
};

const tabBarStyle: CSSProperties = {
  display: "grid",
  gap: 8,
  gridTemplateColumns: "1fr 1fr",
  padding: 8,
  borderRadius: 18,
  background: "rgba(2,6,23,0.52)",
  border: "1px solid rgba(96,165,250,0.14)",
};

const compactPanelBodyStyle: CSSProperties = {
  padding: 11,
  borderRadius: 18,
  background:
    "radial-gradient(circle at top left, rgba(37,99,235,0.16), transparent 34%), linear-gradient(180deg, rgba(15,23,42,0.88), rgba(2,6,23,0.82))",
  border: "1px solid rgba(96,165,250,0.18)",
  display: "grid",
  gap: 11,
};

const sliderGridStyle: CSSProperties = {
  display: "grid",
  gap: 10,
};

const cardButtonGridStyle: CSSProperties = {
  display: "grid",
  gap: 8,
  gridTemplateColumns: "1fr 1fr",
};

const statusCardStyle: CSSProperties = {
  display: "grid",
  gap: 7,
  padding: "13px 15px",
  borderRadius: 18,
  border: "1px solid rgba(34,211,238,0.3)",
  background:
    "radial-gradient(circle at top left, rgba(34,211,238,0.2), transparent 34%), linear-gradient(180deg, rgba(15,23,42,0.94), rgba(2,6,23,0.9))",
  boxShadow: "0 18px 45px rgba(2,6,23,0.34), inset 0 1px 0 rgba(255,255,255,0.06)",
};

const statusTitleRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const statusDotStyle: CSSProperties = {
  width: 9,
  height: 9,
  borderRadius: 999,
  background: "#22d3ee",
  boxShadow: "0 0 18px rgba(34,211,238,0.9)",
};

const statusTitleStyle: CSSProperties = {
  color: "#e0f2fe",
  fontSize: 12,
  fontWeight: 950,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const statusTextStyle: CSSProperties = {
  color: "#bfdbfe",
  fontSize: 14,
  fontWeight: 800,
  lineHeight: 1.45,
};

function PrecisionSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  onCommit,
  display,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onCommit: () => void;
  display?: string;
}) {
  const [inputValue, setInputValue] = useState(String(value));
  const commitLockRef = useRef(false);

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const clamp = (val: number) => Math.max(min, Math.min(max, val));

  const commitValue = (rawValue: string | number) => {
    const numeric = typeof rawValue === "number" ? rawValue : Number(rawValue);
    const safe = Number.isFinite(numeric) ? clamp(numeric) : value;

    setInputValue(String(safe));
    onChange(safe);

    if (commitLockRef.current) return;
    commitLockRef.current = true;

    window.requestAnimationFrame(() => {
      onCommit();
      commitLockRef.current = false;
    });
  };

  return (
    <label style={{ display: "grid", gap: 5 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 900, color: "#dbeafe" }}>{label}</span>

        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onBlur={() => commitValue(inputValue)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
              commitValue(inputValue);
            }

            if (event.key === "ArrowUp") {
              event.preventDefault();
              commitValue(value + (event.shiftKey ? step * 10 : step));
            }

            if (event.key === "ArrowDown") {
              event.preventDefault();
              commitValue(value - (event.shiftKey ? step * 10 : step));
            }
          }}
          style={{
            width: 72,
            padding: "5px 7px",
            borderRadius: 11,
            border: "1px solid rgba(96,165,250,0.26)",
            background: "rgba(15,23,42,0.86)",
            color: "#e0f2fe",
            fontSize: 12,
            fontWeight: 900,
            textAlign: "center",
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => {
            const next = Number(event.target.value);
            setInputValue(String(next));
            onChange(next);
          }}
          onPointerUp={() => commitValue(value)}
          onTouchEnd={() => commitValue(value)}
          onMouseUp={() => commitValue(value)}
          onKeyUp={() => commitValue(value)}
          style={{ width: "100%", accentColor: "#22d3ee" }}
        />

        <span
          style={{
            minWidth: 44,
            textAlign: "center",
            fontSize: 11,
            fontWeight: 900,
            color: "#bfdbfe",
            padding: "3px 6px",
            borderRadius: 999,
            background: "rgba(37,99,235,0.16)",
            border: "1px solid rgba(96,165,250,0.18)",
          }}
        >
          {display || value}
        </span>
      </div>
    </label>
  );
}

function WorkflowStep({
  active,
  label,
  help,
  onClick,
}: {
  active: boolean;
  label: string;
  help: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={help}
      style={{
        padding: "7px 10px",
        borderRadius: 13,
        border: active ? "1px solid rgba(34,211,238,0.55)" : "1px solid rgba(96,165,250,0.16)",
        background: active
          ? "linear-gradient(180deg, rgba(37,99,235,0.92), rgba(14,116,144,0.62))"
          : "rgba(15,23,42,0.62)",
        color: active ? "#ffffff" : "#93c5fd",
        fontSize: 12,
        fontWeight: 900,
        textAlign: "center",
        cursor: "help",
      }}
    >
      {label} <span style={{ opacity: 0.85 }}>ⓘ</span>
    </button>
  );
}

function ToolTab({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? "1px solid rgba(34,211,238,0.42)" : "1px solid rgba(96,165,250,0.16)",
        background: active
          ? "linear-gradient(180deg, rgba(37,99,235,0.98), rgba(29,78,216,0.86))"
          : "rgba(15,23,42,0.72)",
        color: active ? "#ffffff" : "#dbeafe",
        borderRadius: 14,
        padding: "10px 12px",
        fontWeight: 900,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function PanelHeader({ title, text }: { title: string; text: string }) {
  return (
    <div>
      <div style={{ color: "#eff6ff", fontSize: 15, fontWeight: 900 }}>{title}</div>
      <div style={{ color: "#93c5fd", fontSize: 12, lineHeight: 1.35, marginTop: 3 }}>{text}</div>
    </div>
  );
}

function PremiumOptionButton({
  title,
  text,
  onClick,
  primary = false,
  active = false,
}: {
  title: string;
  text: string;
  onClick: () => void;
  primary?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        borderRadius: 16,
        padding: 10,
        minHeight: 66,
        cursor: "pointer",
        border: active
          ? "2px solid rgba(34,211,238,0.98)"
          : primary
            ? "1px solid rgba(34,211,238,0.42)"
            : "1px solid rgba(96,165,250,0.18)",
        background: active
          ? "radial-gradient(circle at top left, rgba(34,211,238,0.35), transparent 36%), linear-gradient(180deg, rgba(37,99,235,0.98), rgba(14,116,144,0.82))"
          : primary
            ? "linear-gradient(180deg, rgba(37,99,235,0.92), rgba(14,116,144,0.68))"
            : "linear-gradient(180deg, rgba(30,41,59,0.58), rgba(15,23,42,0.78))",
        boxShadow: active
          ? "0 0 0 1px rgba(34,211,238,0.25), 0 14px 36px rgba(34,211,238,0.14)"
          : "none",
      }}
    >
      <div style={{ color: "#eff6ff", fontWeight: 900, fontSize: 12 }}>{active ? "✓ " : ""}{title}</div>
      <div style={{ color: active ? "#dff9ff" : "#93c5fd", fontSize: 11, lineHeight: 1.3, marginTop: 4 }}>{text}</div>
    </button>
  );
}